import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Ic } from '../shell/Icons';
import ItemTooltip from '../itemCard/ItemTooltip';
import IdChip from '../common/IdChip';
import ImpactGraph from './ImpactGraph';
import { usePref } from '../../services/prefs';
import { requirementsApi } from '../../services/api';
import { relationshipVerb } from '../../utils/linkGrammar';

// Impact-tree view (#182): upstream / downstream / verification lenses on an
// item's link graph, each rendered as a collapsible tree. Every node reuses
// ItemTooltip + IdChip (the same "rich tooltip" + friendly-id/GUID-switch
// (#174) surfaces the Relationships tab already uses) — no new tooltip here.

const LENSES = [
  { key: 'upstream', label: 'Upstream', hint: 'What this item derives from / decomposes from' },
  { key: 'downstream', label: 'Downstream', hint: 'What derives from / decomposes from this item' },
  { key: 'verification', label: 'Verification', hint: 'Test cases and validation items linked to this item' },
];

function ImpactNode({ node, showHumanIds, depth }) {
  const [open, setOpen] = useState(depth < 2); // auto-expand the first couple of levels
  const children = Array.isArray(node.children) ? node.children : [];
  const expandable = children.length > 0;

  return (
    <Box sx={{ ml: depth ? 1.5 : 0 }}>
      <Box
        className={`lrow${open ? ' open' : ''}`}
        sx={{ display: 'flex', alignItems: 'center', gap: 6, py: '4px', minWidth: 0 }}
      >
        {expandable ? (
          <span
            data-testid={`impact-toggle-${node.id}`}
            role="button"
            tabIndex={0}
            aria-label={open ? 'Collapse' : 'Expand'}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
            style={{ display: 'inline-flex', cursor: 'pointer', flexShrink: 0 }}
          >
            <Ic name="chev" sm className="chev" style={open ? { transform: 'rotate(90deg)' } : undefined} />
          </span>
        ) : (
          <span style={{ width: 14, flexShrink: 0 }} />
        )}

        <ItemTooltip
          item={{ id: node.id, display_id: node.display_id, title: node.title, type: node.type_id, status: node.status }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <IdChip id={node.id} displayId={node.display_id} showHumanIds={showHumanIds} />
            <span
              className="t-body"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}
            >
              {node.title || '—'}
            </span>
          </span>
        </ItemTooltip>

        {node.link_type && (
          // #374: read the verb from THIS item's side (direction-aware) so a
          // downstream child reads "Child", not the raw stored "parent". The
          // backend ships `edge_direction` per impact node; relationshipVerb flips
          // an INCOMING verb to its opposite and humanises it (same helper the
          // Relationships tab uses, so the two views can't disagree).
          <span className="t-over" style={{ flexShrink: 0 }}>
            {relationshipVerb(node.link_type, node.edge_direction)}
          </span>
        )}
        {node.circular && <span className="t-over" style={{ flexShrink: 0 }}>circular reference</span>}
        {node.truncated && <span className="t-over" style={{ flexShrink: 0 }}>more beyond this depth…</span>}
      </Box>

      {expandable && open && (
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', ml: '6px', pl: 1 }}>
          {children.map((child) => (
            <ImpactNode key={`${child.id}-${child.link_type}`} node={child} showHumanIds={showHumanIds} depth={depth + 1} />
          ))}
        </Box>
      )}
    </Box>
  );
}

ImpactNode.propTypes = {
  node: PropTypes.object.isRequired,
  showHumanIds: PropTypes.bool,
  depth: PropTypes.number.isRequired,
};

function LensSection({ label, hint, nodes, showHumanIds }) {
  return (
    <Box sx={{ mb: 2 }}>
      <div className="t-over" style={{ marginBottom: 4 }} title={hint}>{label} · {nodes.length}</div>
      {nodes.length === 0 ? (
        <div className="t-body muted3">None.</div>
      ) : (
        nodes.map((node) => (
          <ImpactNode key={`${node.id}-${node.link_type}`} node={node} showHumanIds={showHumanIds} depth={0} />
        ))
      )}
    </Box>
  );
}

LensSection.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  nodes: PropTypes.array.isRequired,
  showHumanIds: PropTypes.bool,
};

// Ancestors "all the way to the top" (#182 follow-up, for the Graph view's
// family-tree mode) — matches the backend's MAX_UPSTREAM_DEPTH ceiling (see
// core.requirements.impact_tree). Downstream/verification stay at the original
// depth=3 (the wide-fan-out direction — see that module's docstring for why
// upstream and downstream are treated asymmetrically). Fetched once and shared
// by both the List and Graph view modes below.
const GRAPH_UP_DEPTH = 50;

const ImpactTab = ({ itemId }) => {
  const showHumanIds = usePref('showHumanIds');
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // List/Graph view-mode toggle (#182 follow-up: the impact GRAPH). Additive —
  // the list view below is unchanged; Graph mode renders ImpactGraph fed by the
  // SAME fetched tree, no extra fetch.
  // #182 polish round 2 (item 3): Graph is now the DEFAULT view (was 'list') —
  // still fully togglable back to the list.
  const [viewMode, setViewMode] = useState('graph'); // 'list' | 'graph'
  // #238: lifted from ImpactGraph so its Linear/V-model toggle can render
  // beside the List/Graph one below, in a single row, instead of two stacked
  // rows ("could all be beside each other to save vertical space").
  const [layoutMode, setLayoutMode] = useState('family'); // 'family' | 'vmodel'

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await requirementsApi.getImpactTree(itemId, 3, GRAPH_UP_DEPTH);
      setTree(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load the impact tree.');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>;
  }
  if (!tree) {
    return <div className="t-body muted3">No impact data.</div>;
  }

  return (
    <div style={{ marginTop: 8 }}>
      {/* #238: List/Graph and (when Graph is active) Linear/V-model share ONE row —
          they used to be two stacked rows (this component's own + ImpactGraph's
          internal one), costing vertical space a phone can't spare. The app's
          MuiToggleButton theme override sets a flat 12px/16px padding regardless
          of `size="small"`, which alone is just wide enough that both groups
          together don't quite fit a 390px phone's content width — the `sx`
          padding override below (mobile only; unchanged on desktop) is what
          actually gets them onto one row rather than the flex-wrap silently
          dropping the second group to a new line. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: { xs: 0.5, sm: 1.5 } }}>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_e, v) => { if (v) setViewMode(v); }}
          aria-label="Impact view mode"
          sx={{ '& .MuiToggleButton-root': { px: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 1.5 } } }}
        >
          <ToggleButton value="list" data-testid="impact-view-list" aria-label="List view">List</ToggleButton>
          <ToggleButton value="graph" data-testid="impact-view-graph" aria-label="Graph view">Graph</ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'graph' && (
          <ToggleButtonGroup
            size="small"
            value={layoutMode}
            exclusive
            onChange={(_e, v) => { if (v) setLayoutMode(v); }}
            aria-label="Graph layout"
            sx={{ '& .MuiToggleButton-root': { px: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 1.5 } } }}
          >
            <ToggleButton value="family" data-testid="impact-graph-layout-family" aria-label="Linear layout">
              Linear
            </ToggleButton>
            <ToggleButton value="vmodel" data-testid="impact-graph-layout-vmodel" aria-label="V-model layout">
              V-model
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {viewMode === 'graph' ? (
        <ImpactGraph tree={tree} layoutMode={layoutMode} onLayoutModeChange={setLayoutMode} renderLayoutToggle={false} />
      ) : (
        LENSES.map((lens) => (
          <LensSection
            key={lens.key}
            label={lens.label}
            hint={lens.hint}
            nodes={tree[lens.key] || []}
            showHumanIds={showHumanIds}
          />
        ))
      )}
    </div>
  );
};

ImpactTab.propTypes = {
  itemId: PropTypes.string,
};

export default ImpactTab;
